import { cacaClient } from '@plangrid-private/web-api-clients';

import { displayDate } from '@adsk/alloy-react-datetime';

import {
  FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
  FORM_FILTERS_DEFAULT_VALUES,
  FORM_COLLABORATION_MODES,
  ELEMENT_TYPES,
  VALUE_NAMES,
  TREE_PAGE_SIZE,
  FORM_STATUSES,
  FORM_TEMPLATE_PERMISSIONS,
  ALLOW_DRAFT_ASSIGNEE_KEY,
  ASSIGNEE_DRAFT_COEDITING_KEY,
  SINGLE_CONTRIBUTOR_KEY,
  ASSIGNEE_TYPES,
  DEFAULT_FORM_ASSIGNEE_TO_CREATOR_KEY,
} from './constants';
import { formsClient } from './formsClient';

export const chunk = (arr, size) => {
  const output = [];
  for (let i = 0; i < arr.length; i += size) {
    output.push(arr.slice(i, i + size));
  }
  return output;
};

export const getTemplateUrl = (projectId, templateId, folderId) => {
  return !folderId
    ? `/build/forms/projects/${projectId}/field-reports?template_uid=${templateId}`
    : `/build/forms/projects/${projectId}/field-reports?folder_uid=${folderId}&template_uid=${templateId}`;
};

export const getFormDetailsUrl = (projectId, formTypeId, formId) => {
  return `/build/forms/projects/${projectId}/field-reports/${formTypeId}/reports/${formId}`;
};

export const getProjectUserUrl = (projectId) => {
  return `/v2/project/${projectId}/user`;
};

export const getForgeUserMappingUrl = (projectId) => {
  return `/v7/project/${projectId}/users-forge-mapping`;
};

export const getPGProjectUsersUrl = (projectId) => {
  return `/v2/project/${projectId}/users`;
};

export const getDocumentsUrl = (projectId) => {
  return `/v3/project/${projectId}/documents`;
};

export const getCreateFormUrl = (projectId, formTypeId, formId) => {
  return `/v5/project/${projectId}/field-report-types/${formTypeId}/reports/${formId}`;
};

export const getFormTemplatesUrl = (projectId) => {
  return `/v2/project/${projectId}/field-report-types`;
};

export const getDateUTC = (date) => {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

// backend truncates the epoch time to 12am UTC, we need to encounter for the offsets
export const getUTCDate = (localMs) => {
  const date = new Date(localMs);
  const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return date.getTime() - tzOffsetMs;
};

export const getLocalDate = (utcMs) => {
  const date = new Date(utcMs);
  const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return date.getTime() + tzOffsetMs;
};

export const getItemListString = (itemList) => {
  if (itemList.length === 0) {
    return '';
  } else if (itemList.length === 1) {
    return `"${itemList[0]}"`;
  }

  return itemList
    .map((item, index) => {
      if (index === itemList.length - 1) {
        return `and "${item}"`;
      }
      return `"${item}"`;
    })
    .join(', ');
};

export const getFormDateString = (formDate, locale) => {
  if (!formDate) return '';

  // Form date values are the same across all timezones
  const date = new Date(getLocalDate(formDate));
  return displayDate(date, { locale });
};

export const getFormCollaborationMode = (template) => {
  if (template.allow_assignee_draft_coediting) {
    return FORM_COLLABORATION_MODES.MULTIPLE_PARALLEL;
  }

  if (template.allow_draft_assignee) {
    return FORM_COLLABORATION_MODES.MULTIPLE_SEQUENCE;
  }

  return FORM_COLLABORATION_MODES.SINGLE;
};

export const userHasPermissionOnTemplate = (
  pgUserId,
  pgUserRoleId,
  permission,
  template,
) => {
  // Check if user has permission
  const templateUsers = template?.users || [];
  const userHasPermission = templateUsers.find(
    ({ user_id, permissions }) =>
      user_id === pgUserId && permissions.includes(permission),
  );

  if (userHasPermission) return true;

  // Check if user's group has permission
  const templateGroups = template?.groups || [];
  const groupHasPermission = templateGroups.find(
    ({ role_uid, permissions }) =>
      role_uid === pgUserRoleId && permissions.includes(permission),
  );

  return groupHasPermission;
};

export const getFormTemplateUrn = (templateUid) => {
  return `urn:adsk:form.template:${templateUid}`;
};

export const parseFormTemplateUrn = (formTemplateUrn) => {
  if (!formTemplateUrn) return null;

  // formtemplate URN format: urn:adsk:form.template:{template_uid}
  const arr = formTemplateUrn.match(/urn:adsk:form.template:(.*)/);

  if (arr && arr.length === 2) {
    return arr[1];
  }

  return null;
};

export const parseFormFieldUrn = (formFieldUrn) => {
  if (!formFieldUrn)
    return {
      formId: null,
      schemaId: null,
    };

  // formfield URN format: urn:adsk:form.field:{form_id}/{pg_form_field_schema_id}
  const arr = formFieldUrn.match(/urn:adsk:form.field:(.*)\/(.*)/);

  if (arr && arr.length === 3) {
    return {
      formId: arr[1],
      schemaId: arr[2],
    };
  }

  return {
    formId: null,
    schemaId: null,
  };
};

const getFieldIndices = (section) => {
  const nonSignatureFields = section.children.filter(
    (field) => field.value_name !== VALUE_NAMES.SVG_VAL,
  );
  return Array.from(Array(nonSignatureFields.length).keys());
};

export const getFormFieldsFromLayout = (
  layout = {},
  sectionIndex,
  fieldIndices,
) => {
  if (layout.type === ELEMENT_TYPES.FIELD) {
    let fieldIndex = fieldIndices[0] || 0;
    if (layout.value_name !== VALUE_NAMES.SVG_VAL) {
      fieldIndex = fieldIndices.shift();
    }

    return [{ ...layout, prefix: `${sectionIndex + 1}.${fieldIndex + 1}` }];
  }

  if (!layout.children) {
    return [];
  }

  const fields = [];
  layout.children.forEach((child, idx) => {
    switch (child.type) {
      case ELEMENT_TYPES.SECTION:
        fields.push(
          ...getFormFieldsFromLayout(child, idx, getFieldIndices(child)),
        );
        break;
      case ELEMENT_TYPES.FIELD:
        fields.push(
          ...getFormFieldsFromLayout(child, sectionIndex, fieldIndices),
        );
        break;
      case ELEMENT_TYPES.TABLE:
      default:
    }
  });

  return fields;
};

export const fetchFormsByIds = async (projectId, formIds, params = {}) => {
  if (!formIds.length) return [];

  const formIdsBatches = chunk(formIds, FORM_FILTERS_DEFAULT_VALUES.limit);

  const fetchFormsBatch = async (formIdsBatch) => {
    const { items } = await formsClient.getForms(projectId, 'all', {
      ...FORM_FILTERS_DEFAULT_VALUES,
      uids: formIdsBatch,
      ...params,
    });

    return items;
  };

  const formBatches = await Promise.all(formIdsBatches.map(fetchFormsBatch));
  return [].concat(...formBatches);
};

export const fetchFormTemplatesByIds = async (
  projectId,
  templateIds,
  _templates = [],
) => {
  if (!templateIds.length) return [];

  const templateIdsBatches = chunk(templateIds, TREE_PAGE_SIZE);

  const fetchFormTemplatesBatch = async (formIdsBatch) => {
    const { items } = await cacaClient.fieldReports.getFieldReportTypes(
      projectId,
      {
        ...FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
        uids: formIdsBatch,
      },
    );

    return items;
  };

  const formTemplateBatches = await Promise.all(
    templateIdsBatches.map(fetchFormTemplatesBatch),
  );
  return [].concat(...formTemplateBatches);
};

export const getFormAssignee = (form) => {
  if (form?.assignee_id) {
    return form.assignee_id;
  }
  return form?.created_by;
};

export const getWorkflowKey = (
  allowDraftAssignee,
  allowAssigneeDraftCoediting,
) => {
  if (allowAssigneeDraftCoediting) {
    return ASSIGNEE_DRAFT_COEDITING_KEY;
  }
  if (allowDraftAssignee) {
    return ALLOW_DRAFT_ASSIGNEE_KEY;
  }
  if (!allowAssigneeDraftCoediting && !allowDraftAssignee) {
    return SINGLE_CONTRIBUTOR_KEY;
  }
  return null;
};

export const selectWorkflowKey = (template) => {
  const {
    [ALLOW_DRAFT_ASSIGNEE_KEY]: allowDraftAssignee,
    [DEFAULT_FORM_ASSIGNEE_TO_CREATOR_KEY]: defaultFormAssigneeToCreator,
  } = template;

  if (!allowDraftAssignee) {
    return SINGLE_CONTRIBUTOR_KEY;
  }

  if (defaultFormAssigneeToCreator || !template.pg_form) {
    return ALLOW_DRAFT_ASSIGNEE_KEY;
  }

  // we could check the coediting key ASSIGNEE_DRAFT_COEDITING_KEY here,
  // but it should be true for all native forms that allow draft.
  // likewise, it should be false for all PDFs.
  return ASSIGNEE_DRAFT_COEDITING_KEY;
};

export const isTemplateCollaborative = (template) => {
  const templateWorkflow = selectWorkflowKey(template);

  return templateWorkflow !== SINGLE_CONTRIBUTOR_KEY;
};

export const checkUserPermissionOnTemplate = (
  userPermissionInfo,
  template,
  permission,
) => {
  if (!userPermissionInfo || !template || !permission) return false;

  // Check user permissions
  if (template.users) {
    const userPermissions = template.users.find(
      ({ user_id }) => user_id === userPermissionInfo.userPGID,
    );
    if (userPermissions?.permissions.includes(permission)) return true;
  }

  // Check group permissions
  if (template.groups) {
    const groupPermissions = template.groups.find(
      ({ role_uid }) => role_uid === userPermissionInfo.pgRoleId,
    );
    if (groupPermissions?.permissions.includes(permission)) return true;
  }

  // Check company permissions
  if (template.companies) {
    const companyPermissions = template.companies.find(
      ({ company_id }) => company_id === userPermissionInfo.companyId,
    );
    if (companyPermissions?.permissions.includes(permission)) return true;
  }

  // Check role permissions
  if (template.roles && userPermissionInfo.roleIds) {
    const hasPermission = userPermissionInfo.roleIds.some((roleId) => {
      const rolePermissions = template.roles.find(
        ({ role_id }) => role_id === roleId,
      );
      return rolePermissions?.permissions.includes(permission);
    });

    if (hasPermission) return true;
  }
  return false;
};

export const isFormReadOnly = (
  userPermissionInfo,
  form,
  template,
  isStatusExpansionEnabled,
) => {
  if (!userPermissionInfo || !form || !template) return true;

  // Anything that isn't in draft is read only
  if (form.status !== FORM_STATUSES.DRAFT) {
    return true;
  }

  // If you can't submit the form, it is read only
  let canManage = false;
  const isPdf = !template.pg_form;
  const isCreator = userPermissionInfo.userPGID === form.created_by;
  const formAssigneeId = getFormAssignee(form);
  const isAssignee = formAssigneeId === userPermissionInfo.userPGID;
  const workflow = getWorkflowKey(
    template[ALLOW_DRAFT_ASSIGNEE_KEY],
    template[ASSIGNEE_DRAFT_COEDITING_KEY],
  );
  const isAssigneeThroughGroup =
    (form.assignee_type === ASSIGNEE_TYPES.COMPANY &&
      formAssigneeId === userPermissionInfo.companyId) ||
    (form.assignee_type === ASSIGNEE_TYPES.ROLE &&
      userPermissionInfo.roleIds.includes(formAssigneeId)) ||
    isAssignee;
  const shouldCheckManagePermission =
    isCreator || isAssignee || (isTemplateCollaborative(template) && !isPdf);

  if (isStatusExpansionEnabled && shouldCheckManagePermission) {
    canManage = checkUserPermissionOnTemplate(
      userPermissionInfo,
      template,
      FORM_TEMPLATE_PERMISSIONS.MANAGE,
    );
  }

  const canSubmit =
    canManage ||
    checkUserPermissionOnTemplate(
      userPermissionInfo,
      template,
      FORM_TEMPLATE_PERMISSIONS.SUBMIT,
    );

  if (!canSubmit) {
    return true;
  }

  switch (workflow) {
    case ALLOW_DRAFT_ASSIGNEE_KEY:
      // Multiple contributors in sequence, only the assignee can edit
      return !isAssigneeThroughGroup;
    case ASSIGNEE_DRAFT_COEDITING_KEY:
      if (isStatusExpansionEnabled) {
        if (
          (canManage = checkUserPermissionOnTemplate(
            userPermissionInfo,
            template,
            FORM_TEMPLATE_PERMISSIONS.MANAGE,
          ))
        ) {
          return false;
        }
        return !isAssigneeThroughGroup;
      }

      // Multiple contributors in parallel, all submitters can edit
      return false;
    case SINGLE_CONTRIBUTOR_KEY:
      // Single collaborator, only the creator can edit
      return !isCreator;
    default:
      // Read only because this is some new workflow this code doesn't understand
      return true;
  }
};

export const isUserInCompany = (userPermissionInfo, companyId) => {
  if (!userPermissionInfo || !companyId) return false;

  return userPermissionInfo.companyId === companyId;
};

export const isUserInRole = (userPermissionInfo, roleId) => {
  if (!userPermissionInfo || !roleId) return false;

  return userPermissionInfo.roleIds.includes(roleId);
};
